<template>
    <div class="usecase-container">
        <div class="layout-wrapper">
            <!-- 菜单区域组件 -->
            <menu-section 
                v-for="(menu, index) in menuConfig" 
                :key="index"
                :title="menu.title"
                :icon="menu.icon"
                :list="menu.list"
                :loading="menu.loading"
                :active="menu.active"
                :show="menu.show"
                :class="menu.class"
                :type="menu.type"
                @add="menu.onAdd"
                @select="menu.onSelect"
                @edit="menu.onEdit"
                @delete="menu.onDelete">
            </menu-section>

            <!-- 右侧内容区域 -->
            <div class="right-content-area">
                <div class="content-header">
                    <div class="title-container">
                        <h3 class="content-title">{{ currentContentTitle }}</h3>
                        <span v-if="currentModelForTitle" class="model-tag">
                            {{ currentModelForTitle }}
                        </span>
                    </div>
                    
                    <!-- 操作按钮区域 -->
                    <div v-if="activeSecondaryMenu" class="header-actions">
                        <el-button 
                            type="primary" 
                            size="small" 
                            icon="el-icon-link"
                            @click="handleAddContent">
                            关联网关
                        </el-button>
                    </div>
                </div>
                
                <div class="content-body">
                    <div v-if="activeSecondaryMenu" class="content-container">
                        <!-- 有数据时显示网关列表 -->
                        <gateway-list
                            v-if="!contentLoading && contentTableData.length > 0"
                            :table-data="contentTableData"
                            :loading="contentLoading"
                            :pagination="pagination"
                            @selection-change="handleSelectionChange"
                            @remove="handleRemoveGateway"
                            @batch-delete="handleBatchDeleteGateway"
                            @size-change="handleSizeChange"
                            @current-change="handleCurrentChange">
                        </gateway-list>
                        
                        <!-- 加载状态 -->
                        <div v-else-if="contentLoading" class="loading-container" v-loading="true" element-loading-text="加载中..." element-loading-spinner="el-icon-loading" element-loading-background="rgba(255, 255, 255, 0.8)" element-loading-custom-class="custom-loading">
                        </div>
                        
                        <!-- 空状态 -->
                        <empty-state 
                            v-else
                            icon="el-icon-cpu"
                            text="暂无关联网关数据"
                            button-text="去关联"
                            button-icon="el-icon-position"
                            @create="handleAddContent">
                        </empty-state>
                    </div>
                    
                    <empty-state 
                        v-else-if="!activePrimaryMenu"
                        icon="el-icon-office-building"
                        text="请选择场景"
                        :show-button="false">
                    </empty-state>
                    
                    <empty-state 
                        v-else
                        icon="el-icon-school"
                        text="请选择子场景查看内容"
                        :show-button="false">
                    </empty-state>
                </div>
            </div>
        </div>

        <!-- 弹窗组件 -->
        <menu-dialog
          :visible.sync="menuDialog.visible"
          :title="menuDialog.title"
          :form="menuDialog.form"
          :rules="menuDialog.rules"
          :model-options="modelOptions"
          :is-secondary="menuDialog.isSecondary"
          @submit="handleMenuDialogSubmit"
          @close="handleMenuDialogClose">
        </menu-dialog>

        <!-- 网关选择器组件 -->
        <gateway-selector
            :visible.sync="gatewayDialogVisible"
            :model-id="currentModelId"
            :model-name="currentModelName"
            :scenario-id="activeSecondaryMenu"
            @confirm="handleGatewayConfirm"
            @close="handleGatewayClose">
        </gateway-selector>
    </div>
</template>

<script>
import { loadUserScenarioList, loadUserScenarioAdd, loadUserScenarioUpdate, loadUserScenarioDelete, gatewayListModelRelateGateway, loadUserScenarioGateWayDelete } from "@/api/waveform/userScenario";
import { modelAiListAll } from "@/api/waveform/model";
import GatewaySelector from "./components/GatewaySelector.vue";
import GatewayList from "./components/GatewayList.vue";
import MenuSection from "./components/MenuSection.vue";
import EmptyState from "./components/EmptyState.vue";
import MenuDialog from "./components/MenuDialog.vue";

export default {
    name: "Usecase",
    components: {
        GatewaySelector,
        GatewayList,
        MenuSection,
        EmptyState,
        MenuDialog
    },
    data() {
        return {
            // 菜单数据
            primaryMenuList: [],
            secondaryMenuList: [],
            primaryMenuLoading: false,
            secondaryMenuLoading: false,
            activePrimaryMenu: null,
            activeSecondaryMenu: null,
            
            // 内容数据
            contentTableData: [],
            selectedRows: [], // 改为selectedRows，与表格选择保持一致
            currentModelInfo: null, // 当前选中子场景的模型信息
            
            // 分页数据
            pagination: {
                page: 1,
                size: 10,
                total: 0
            },
            contentLoading: false,
            
            // 弹窗数据
            menuDialog: {
                visible: false,
                title: '',
                isSecondary: false,
                form: { id: '', name: '', description: '', parentId: '', modelId: '' },
                rules: this.getMenuRules()
            },
            
            // 选项数据
            modelOptions: [],
            
            // 网关选择器
            gatewayDialogVisible: false,
            currentModelId: null,
            currentModelName: null
        };
    },
    computed: {
        menuConfig() {
            return [
                {
                    title: '场景',
                    icon: 'el-icon-menu',
                    list: this.primaryMenuList,
                    loading: this.primaryMenuLoading,
                    active: this.activePrimaryMenu,
                    show: true,
                    class: 'left-menu-area',
                    type: 'primary',
                    onAdd: this.handleAddPrimaryMenu,
                    onSelect: this.handleSelectPrimaryMenu,
                    onEdit: this.handleEditPrimaryMenu,
                    onDelete: this.handleDeletePrimaryMenu
                },
                {
                    title: '子场景',
                    icon: 'el-icon-collection',
                    list: this.secondaryMenuList,
                    loading: this.secondaryMenuLoading,
                    active: this.activeSecondaryMenu,
                    show: !!this.activePrimaryMenu,
                    class: 'middle-menu-area',
                    type: 'secondary',
                    onAdd: this.handleAddSecondaryMenu,
                    onSelect: this.handleSelectSecondaryMenu,
                    onEdit: this.handleEditSecondaryMenu,
                    onDelete: this.handleDeleteSecondaryMenu
                }
            ]
        },
        currentContentTitle() {
            if (!this.activePrimaryMenu && !this.activeSecondaryMenu) {
                return '请选择场景'; 
            }
            
            const primary = this.primaryMenuList.find(item => item.id === this.activePrimaryMenu);
            const secondary = this.secondaryMenuList.find(item => item.id === this.activeSecondaryMenu);
            
            if (secondary) {
                return `${primary?.name || ''} - ${secondary.name}`;
            }
            
            return primary?.name || '请选择子场景';
        },
        // 获取当前模型信息用于标题显示
        currentModelForTitle() {
            if (this.activeSecondaryMenu && this.currentModelInfo?.modelName) {
                return this.currentModelInfo.modelName;
            }
            return null;
        }
    },
    mounted() {
        this.initData();
    },
    methods: {
        // 初始化
        initData() {
            this.loadPrimaryMenuList();
            this.loadModelOptions();
        },

        getMenuRules() {
            return {
                name: [
                    { 
                        validator: (rule, value, callback) => {
                            if (!value || value.trim() === '') {
                                callback(new Error('请输入菜单名称'));
                            } else if (value.length > 50) {
                                callback(new Error('长度在 1 到 50 个字符'));
                            } else {
                                callback();
                            }
                        }, 
                        trigger: 'blur' 
                    }
                ],
                modelId: [
                    { 
                        validator: (rule, value, callback) => {
                            if (!value) {
                                callback(new Error('请选择关联模型'));
                            } else {
                                callback();
                            }
                        }, 
                        trigger: 'blur' 
                    }
                ]
            }
        },

        // 通用菜单加载
        async loadMenuList(id = 0, isPrimary = true) {
            const loadingKey = isPrimary ? 'primaryMenuLoading' : 'secondaryMenuLoading';
            this[loadingKey] = true;
            
            try {
                const response = await loadUserScenarioList(id);
                
                if (response.code === 200 && response.data) {
                    const menuData = response.data.map(item => ({
                        id: item.id,
                        name: item.labelName,
                        description: item.description,
                        modelId: item.modelId,
                        modelName: item.modelName,
                        parentId: item.parentId
                    }));
                    
                    if (isPrimary) {
                        this.primaryMenuList = menuData;
                    } else {
                        this.secondaryMenuList = menuData;
                    }
                }
            } catch (error) {
                console.error(`加载${isPrimary ? '场景' : '子场景'}菜单失败:`, error);
                this.$message.error(`加载${isPrimary ? '场景' : '子场景'}菜单失败，请稍后重试`);
            } finally {
                this[loadingKey] = false;
            }
        },

        async loadPrimaryMenuList() {
            await this.loadMenuList(0, true);
        },

        async loadSecondaryMenuList(parentId) {
            await this.loadMenuList(parentId, false);
        },

        async loadModelOptions() {
            try {
                const response = await modelAiListAll({});
                if (response.code === 200 && response.data) {
                    this.modelOptions = response.data.map(item => ({
                        id: item.id,
                        name: item.modelName || item.name
                    }));
                }
            } catch (error) {
                console.error('加载模型选项失败:', error);
                this.$message.error('加载模型选项失败');
            }
        },

        // 菜单操作
        handleAddPrimaryMenu() {
            this.openMenuDialog('新增场景', false);
        },

        handleAddSecondaryMenu() {
            if (!this.activePrimaryMenu) {
                this.$message.warning('请先选择场景');
                return;
            }
            this.openMenuDialog('新增子场景', true);
        },

        handleEditPrimaryMenu(item) {
            this.openMenuDialog('编辑场景', false, item);
        },

        handleEditSecondaryMenu(item) {
            this.openMenuDialog('编辑子场景', true, item);
        },

        openMenuDialog(title, isSecondary, item = null) {
            this.menuDialog = {
                visible: true,
                title,
                isSecondary,
                form: item ? { ...item } : { 
                    id: '', 
                    name: '', 
                    description: '', 
                    parentId: isSecondary ? this.activePrimaryMenu : '',
                    modelId: '' 
                },
                rules: this.getMenuRules()
            };
        },

        async handleMenuDialogSubmit() {
            const isEdit = !!this.menuDialog.form.id;
            const isSecondary = this.menuDialog.isSecondary;
            
            try {
                const params = {
                    labelName: this.menuDialog.form.name,
                    description: this.menuDialog.form.description,
                    parentId: isSecondary ? this.activePrimaryMenu : 0
                };
                
                if (isSecondary) {
                    params.modelId = this.menuDialog.form.modelId;
                }
                
                if (isEdit) {
                    params.id = this.menuDialog.form.id;
                }
                
                const response = isEdit 
                    ? await loadUserScenarioUpdate(params)
                    : await loadUserScenarioAdd(params);
                
                if (response.code === 200) {
                    if (isSecondary) {
                        await this.loadSecondaryMenuList(this.activePrimaryMenu);
                        
                        // 如果编辑的是当前选中的子场景，更新currentModelInfo
                        if (isEdit && this.activeSecondaryMenu === this.menuDialog.form.id) {
                            // 从modelOptions中找到对应的模型名称
                            const selectedModel = this.modelOptions.find(model => model.id === this.menuDialog.form.modelId);
                            this.currentModelInfo = {
                                modelId: this.menuDialog.form.modelId,
                                modelName: selectedModel ? selectedModel.name : '',
                                description: this.menuDialog.form.description
                            };
                        }
                    } else {
                        await this.loadPrimaryMenuList();
                    }
                    this.$message.success(`${isEdit ? '编辑' : '新增'}成功`);
                    this.menuDialog.visible = false;
                } else {
                    this.$message.error(response.message || `${isEdit ? '编辑' : '新增'}失败`);
                }
            } catch (error) {
                console.error(`${isEdit ? '编辑' : '新增'}菜单失败:`, error);
                this.$message.error(`${isEdit ? '编辑' : '新增'}失败，请稍后重试`);
            }
        },

        handleMenuDialogClose() {
            this.menuDialog.visible = false;
        },

        async handleDeleteMenu(item, isSecondary) {
            const menuType = isSecondary ? '子场景' : '场景';
            
            this.$confirm(`确定移除${menuType}菜单"${item.name}"吗？`, '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                try {
                    const response = await loadUserScenarioDelete(item.id);
                    
                    if (response.code === 200) {
                        if (isSecondary) {
                            await this.loadSecondaryMenuList(this.activePrimaryMenu);
                            if (this.activeSecondaryMenu === item.id) {
                                this.activeSecondaryMenu = null;
                                this.contentTableData = [];
                            }
                        } else {
                            await this.loadPrimaryMenuList();
                            if (this.activePrimaryMenu === item.id) {
                                this.activePrimaryMenu = null;
                                this.secondaryMenuList = [];
                                this.activeSecondaryMenu = null;
                                this.contentTableData = [];
                            }
                        }
                        
                        this.$message.success('移除成功');
                    }
                } catch (error) {
                    console.error(`移除${menuType}菜单失败:`, error);
                    this.$message.error('移除失败，请稍后重试');
                }
            }).catch(() => {});
        },

        handleDeletePrimaryMenu(item) {
            this.handleDeleteMenu(item, false);
        },

        handleDeleteSecondaryMenu(item) {
            this.handleDeleteMenu(item, true);
        },

        handleSelectPrimaryMenu(item) {
            if (this.activePrimaryMenu === item.id) return;
            
            this.activePrimaryMenu = item.id;
            this.activeSecondaryMenu = null;
            this.contentTableData = [];
            this.selectedRows = [];
            this.currentModelInfo = null; // 清空模型信息
            
            this.loadSecondaryMenuList(item.id);
        },

        handleSelectSecondaryMenu(item) {
            if (this.activeSecondaryMenu === item.id) return;
            
            this.activeSecondaryMenu = item.id;
            this.selectedRows = [];
            
            // 直接使用子场景item中的模型信息
            this.currentModelInfo = {
                modelId: item.modelId,
                modelName: item.modelName,
                description: item.description
            };
            
            // 重置分页并加载网关列表
            this.pagination.page = 1;
            this.loadGatewayList();
        },

        // 加载网关列表
        async loadGatewayList() {
            if (!this.activeSecondaryMenu || !this.currentModelInfo?.modelId) {
                return;
            }
            
            this.contentLoading = true;
            
            try {
                const params = {
                    page: this.pagination.page,
                    size: this.pagination.size,
                    scenarioId: this.activeSecondaryMenu,
                    modelId: this.currentModelInfo.modelId
                };
                
                const response = await gatewayListModelRelateGateway(params);
                
                if (response.code === 200 && response.data) {
                    this.contentTableData = response.data.records || response.data.list || [];
                    this.pagination.total = response.data.total || 0;
                } else {
                    this.contentTableData = [];
                    this.pagination.total = 0;
                    console.error('加载网关列表失败:', response.message);
                }
            } catch (error) {
                console.error('加载网关列表失败:', error);
                this.$message.error('加载网关列表失败，请稍后重试');
                this.contentTableData = [];
                this.pagination.total = 0;
            } finally {
                this.contentLoading = false;
            }
        },

        // 分页处理
        handleSizeChange(size) {
            this.pagination.size = size;
            this.pagination.page = 1;
            this.loadGatewayList();
        },

        handleCurrentChange(page) {
            this.pagination.page = page;
            this.loadGatewayList();
        },

        handleAddContent() {
            if (!this.activeSecondaryMenu) {
                this.$message.warning('请先选择子场景');
                return;
            }
            
            const currentSecondaryMenu = this.secondaryMenuList.find(item => item.id === this.activeSecondaryMenu);
            if (!currentSecondaryMenu?.modelId) {
                this.$message.warning('当前子场景未关联模型，无法添加内容');
                return;
            }
            
            this.currentModelId = currentSecondaryMenu.modelId;
            this.currentModelName = currentSecondaryMenu.modelName;
            this.gatewayDialogVisible = true;
        },

        // 表格选择处理
        handleSelectionChange(selection) {
            this.selectedRows = selection;
        },

        // 移除网关
        async handleRemoveGateway(row) {
            this.$confirm(`确定要移除网关"${row.gatewayName || row.name}"吗？`, '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                try {
                    const response = await loadUserScenarioGateWayDelete(row.modelGatewayId);
                    
                    if (response.code === 200) {
                        this.$message.success('移除成功');
                        // 刷新列表
                        this.loadGatewayList();
                    }
                } catch (error) {
                    console.error('移除网关失败:', error);
                    this.$message.error('移除失败，请稍后重试');
                }
            }).catch(() => {});
        },

        // 批量移除网关
        async handleBatchDeleteGateway(selectedRows) {
            if (!selectedRows || selectedRows.length === 0) {
                this.$message.warning('请先选择要移除的数据');
                return;
            }

            const gatewayNames = selectedRows.map(row => row.gatewayName || row.name).join('、');
            
            this.$confirm(`确定要批量移除选中的 ${selectedRows.length} 个网关吗？\n网关名称：${gatewayNames}`, '批量移除确认', {
                confirmButtonText: '确定移除',
                cancelButtonText: '取消',
                type: 'warning',
                dangerouslyUseHTMLString: true
            }).then(async () => {
                try {
                    // 提取所有选中行的modelGatewayId，用逗号连接
                    const ids = selectedRows.map(row => row.modelGatewayId).join(',');
                    
                    const response = await loadUserScenarioGateWayDelete(ids);
                    
                    if (response.code === 200) {
                        this.$message.success(`成功移除 ${selectedRows.length} 个网关`);
                        // 刷新列表
                        this.loadGatewayList();
                    } else {
                        this.$message.error(response.message || '批量移除失败');
                    }
                } catch (error) {
                    console.error('批量移除网关失败:', error);
                    this.$message.error('批量移除失败，请稍后重试');
                }
            }).catch(() => {});
        },

        // 网关选择器
        handleGatewayConfirm(selectedGateways) {
            console.log('网关关联成功，选中的网关:', selectedGateways);
            // 刷新网关列表
            this.loadGatewayList();
        },

        handleGatewayClose() {
            this.currentModelId = null;
            this.currentModelName = null;
        }
    }
};
</script>

<style scoped>
.usecase-container {
    height: 100%;
    background: #f5f7fa;
    border-radius: 10px;
}

.layout-wrapper {
    display: flex;
    height: calc(100vh - 120px);
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.left-menu-area {
    width: 220px;
}

.middle-menu-area {
    width: 220px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.right-content-area {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.content-header {
    padding: 10px 24px;
    border-bottom: 1px solid #e8ecef;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: linear-gradient(135deg, #f8f9fa 0%, #f1f3f4 100%);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    position: relative;
    z-index: 5;
}

.title-container {
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1;
}

.content-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #1a1a1a;
    letter-spacing: 0.5px;
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
}

.header-actions .el-button {
    border-radius: 6px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
}

.header-actions .el-button:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.model-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: linear-gradient(135deg, #00d8c3 0%, #00b8a9 100%);
    color: white;
    padding: 4px 12px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(0, 216, 195, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.2);
    animation: modelTagGlow 2s ease-in-out infinite alternate;
}

.model-tag i {
    font-size: 14px;
}

@keyframes modelTagGlow {
    0% {
        box-shadow: 0 2px 8px rgba(0, 216, 195, 0.3);
    }
    100% {
        box-shadow: 0 4px 16px rgba(0, 216, 195, 0.5);
    }
}

.content-body {
    flex: 1;
    overflow: hidden;
    padding:0 10px;
}

.model-info-section {
    padding: 15px 20px;
    border-bottom: 1px solid #e6e6e6;
    background: #f8f9fa;
}

.model-info-card {
    background: #fff;
    border-radius: 8px;
    padding: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.model-info-header {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f0f0f0;
}

.model-info-header i {
    font-size: 18px;
    color: #409eff;
    margin-right: 8px;
}

.model-title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
}

.model-info-content {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
}

.model-info-item {
    display: flex;
    align-items: center;
    min-width: 200px;
}

.info-label {
    font-size: 14px;
    color: #606266;
    font-weight: 500;
    margin-right: 8px;
    flex-shrink: 0;
}

.info-value {
    font-size: 14px;
    color: #303133;
    word-break: break-all;
}

.content-container {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.loading-container {
    height: 200px;
    position: relative;
}

/* 自定义加载样式，匹配主题色 */
.loading-container >>> .custom-loading .el-loading-spinner {
    color: #00d8c3;
}

.loading-container >>> .custom-loading .el-loading-text {
    color: #00b8a9;
    font-weight: 500;
}

.loading-container >>> .custom-loading .el-loading-spinner .el-icon-loading {
    font-size: 28px;
    color: #00d8c3;
}

/* 加载动画效果 */
.loading-container >>> .custom-loading .el-loading-spinner .el-icon-loading {
    animation: loading-rotate 2s linear infinite;
}

@keyframes loading-rotate {
    0% {
        transform: rotate(0deg);
        color: #00d8c3;
    }
    50% {
        color: #00b8a9;
    }
    100% {
        transform: rotate(360deg);
        color: #00d8c3;
    }
}



.empty-content {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
}

.empty-tip {
    text-align: center;
    color: #909399;
}

.empty-tip p {
    margin-top: 20px;
    font-size: 16px;
}
</style>